import React, { useRef, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import config from "../../../../config";
import { useRegionView } from "services/useRegionView";
import { useMaterialUIController } from "context";

const token = config.MAPBOX_ACCESS_TOKEN;

const MapSelect = ({ selectedPoint, setSelectedPoint }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(new mapboxgl.Popup({ closeButton: true }));
  const selectedPointRef = useRef(null);

  const suppressNextCloseRef = useRef(false);

  const { center, zoom } = useRegionView();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const mapStyle = darkMode
    ? "mapbox://styles/mapbox/dark-v11"
    : "mapbox://styles/mapbox/streets-v12";

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyle,
      center,
      zoom,
      accessToken: token,
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl());

    // Add geolocate control to the map
    const currentGeolocation = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: false,
      showUserHeading: false,
      showAccuracyCircle: false,
    });

    mapRef.current.addControl(currentGeolocation);

    // When the control gets a position, override the view (optional)
    currentGeolocation.on("geolocate", (e) => {
      const { longitude, latitude } = e.coords;
      mapRef.current.flyTo({
        center: [longitude, latitude],
        zoom: 15, // your preferred zoom
        essential: true,
      });
    });

    // Attach close handler ONCE
    popupRef.current.on("close", () => {
      if (suppressNextCloseRef.current) {
        suppressNextCloseRef.current = false; // swallow programmatic close
        return;
      }
      setSelectedPoint?.(null); // user-initiated close
    });

    mapRef.current.on("click", (e) => {
      const { lng, lat } = e.lngLat;
      const last = selectedPointRef.current;
      if (Array.isArray(last) && last[0] === lng && last[1] === lat) return;
      setSelectedPoint?.([lng, lat]);
    });

    return () => {
      suppressNextCloseRef.current = true;

      popupRef.current.remove();
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []); // <- important: do not depend on selectedPoint

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    map.setStyle(mapStyle);
  }, [darkMode]);

  // recenter when region view changes
  useEffect(() => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) return;
    suppressNextCloseRef.current = true;
    popupRef.current.remove();

    // explicitly clear selection on region change
    setSelectedPoint?.(null);

    mapRef.current.flyTo({ center, zoom, essential: true });
  }, [center, zoom]);

  useEffect(() => {
    selectedPointRef.current = selectedPoint;
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) return;

    suppressNextCloseRef.current = true;

    popupRef.current.remove();

    if (!selectedPoint) return;

    const [lng, lat] = selectedPoint;

    popupRef.current
      .setLngLat([lng, lat])
      .setHTML(
        `<h4>Επιλεγμένο Σημείο</h4><p>Longitude: ${lng}<br />Latitude: ${lat}</p>`
      )
      .addTo(mapRef.current);

    mapRef.current.flyTo({
      center: [lng, lat],
      essential: true,
      zoom: 12,
    });
  }, [selectedPoint]);

  return (
    <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />
  );
};

export default MapSelect;
